import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, User, Building2, Briefcase, Calendar, TrendingUp,
  ShieldCheck, Clock, MapPin, AlertCircle, ChevronDown, ChevronUp
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const EMPLOYMENT_TYPE_LABELS = {
  REGULAR: "Regular / Permanent",
  FIXED_TERM: "Fixed-Term Contract",
  CONTRACT: "Contract",
  CASUAL: "Casual",
  INTERN: "Internship",
};

const STATUS_BADGE = {
  ACTIVE: "bg-emerald-100 text-emerald-700 border-emerald-200",
  ON_LEAVE: "bg-yellow-100 text-yellow-700 border-yellow-200",
  SUSPENDED: "bg-orange-100 text-orange-700 border-orange-200",
  NOTICE_PERIOD: "bg-red-100 text-red-700 border-red-200",
  TERMINATED: "bg-slate-100 text-slate-600 border-slate-200",
};

const COMPONENT_LABELS = {
  BASE_SALARY: "Basic Pay",
  HOUSING_ALLOWANCE: "House Rent Allowance (HRA)",
  TRANSPORT_ALLOWANCE: "Conveyance Allowance",
  ACADEMIC_ALLOWANCE: "Academic Service Allowance",
  RESEARCH_ALLOWANCE: "Research Allowance",
  ADMINISTRATIVE_ALLOWANCE: "Administrative Allowance",
  MEAL_ALLOWANCE: "Meal Allowance",
  COMMUNICATION_ALLOWANCE: "Communication Allowance",
  RETENTION_BONUS: "Retention Bonus",
  OTHER_ALLOWANCE: "Other Allowance",
};

export default function EmploymentDetail() {
  const { workerId } = useParams();
  const navigate = useNavigate();

  const [worker, setWorker] = useState(null);
  const [compensation, setCompensation] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compLoading, setCompLoading] = useState(false);
  const [compError, setCompError] = useState(null);
  const [error, setError] = useState(null);
  const [showCompensation, setShowCompensation] = useState(false);

  const token = localStorage.getItem("academiaos_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!workerId) return;
    fetchWorker();
  }, [workerId]);

  const fetchWorker = async () => {
    setLoading(true);
    try {
      const [wRes, sRes] = await Promise.all([
        axios.get(`${API}/api/v1/peo01/workers/${workerId}`, { headers }),
        axios.get(`${API}/api/v1/peo01/workers/${workerId}/status-history`, { headers }).catch(() => ({ data: [] })),
      ]);
      setWorker(wRes.data);
      setStatusHistory(sRes.data || []);
    } catch (e) {
      if (e.response?.status === 404) setError("Worker record not found.");
      else if (e.response?.status === 403) setError("You do not have permission to view this employee.");
      else setError("Failed to load worker details.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompensation = async () => {
    if (compensation) { setShowCompensation(!showCompensation); return; }
    setCompLoading(true);
    setCompError(null);
    try {
      const res = await axios.get(`${API}/api/v1/peo01/workers/${workerId}/compensation`, { headers });
      setCompensation(res.data);
      setShowCompensation(true);
    } catch (e) {
      if (e.response?.status === 403) {
        setCompError("Compensation details require additional authorization (people.compensation.view).");
      } else {
        setCompError("Failed to load compensation details.");
      }
    } finally {
      setCompLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount);

  const yearsOfService = (startDate) => {
    if (!startDate) return null;
    const diff = (new Date() - new Date(startDate)) / (1000 * 60 * 60 * 24 * 365.25);
    const years = Math.floor(diff);
    const months = Math.floor((diff - years) * 12);
    return years > 0 ? `${years}y ${months}m` : `${months} months`;
  };

  const getInitials = (name) =>
    name?.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase() || "??";

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500 text-sm">Loading employment record…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={40} className="text-red-400 mx-auto mb-3" />
          <p className="text-slate-700">{error}</p>
          <button onClick={() => navigate(-1)} className="mt-4 text-indigo-600 text-sm hover:underline">Go back</button>
        </div>
      </div>
    );
  }

  if (!worker) return null;

  const w = worker;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-8 py-5">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            data-testid="back-button"
            className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{w.display_name}</h1>
            <p className="text-sm text-slate-500 font-mono">{w.worker_number}</p>
          </div>
          {w.is_synthetic && (
            <span className="ml-auto text-xs bg-amber-50 text-amber-600 border border-amber-200 px-2.5 py-1 rounded-full font-medium">
              Synthetic Demo Record
            </span>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-8 py-8 space-y-6">
        {/* Profile Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="worker-profile">
          <div className="flex items-start gap-5">
            <div className="w-16 h-16 rounded-2xl bg-indigo-600 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
              {getInitials(w.display_name)}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-2xl font-bold text-slate-900">{w.display_name}</h2>
                {w.employment_status && (
                  <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${STATUS_BADGE[w.employment_status] || ""}`}>
                    {w.employment_status}
                  </span>
                )}
              </div>
              <p className="text-slate-600 font-medium">{w.job_title}</p>
              <p className="text-slate-500 text-sm mt-0.5">{w.department_name}</p>
              {w.grade_name && (
                <p className="text-xs text-slate-400 mt-1">{w.grade_name}</p>
              )}
            </div>
          </div>
        </div>

        {/* Two-column grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Employment Details */}
          <Section icon={<Briefcase size={16} className="text-indigo-500" />} title="Employment" testId="employment-details">
            <InfoRow label="Employee ID" value={<span className="font-mono text-sm">{w.worker_number}</span>} />
            <InfoRow label="Employment Type" value={EMPLOYMENT_TYPE_LABELS[w.employment_type] || w.employment_type} />
            <InfoRow
              label="Start Date"
              value={w.start_date ? new Date(w.start_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
            />
            {w.start_date && (
              <InfoRow label="Service Duration" value={yearsOfService(w.start_date)} />
            )}
            {w.worker_category && (
              <InfoRow label="Worker Category" value={w.worker_category} />
            )}
            {w.legal_entity_name && (
              <InfoRow label="Legal Entity" value={w.legal_entity_name.replace(" (Synthetic Demo)", "")} />
            )}
          </Section>

          {/* Position & Org */}
          <Section icon={<Building2 size={16} className="text-blue-500" />} title="Position & Organisation" testId="position-details">
            {w.position_title && <InfoRow label="Position" value={w.position_title} />}
            {w.position_code && <InfoRow label="Position Code" value={<span className="font-mono text-sm">{w.position_code}</span>} />}
            <InfoRow label="Department" value={w.department_name || "—"} />
            {w.job_family && (
              <InfoRow label="Job Family" value={w.job_family.replace(/_/g, " ")} />
            )}
            {w.cost_center_name && (
              <InfoRow label="Cost Centre" value={w.cost_center_name} />
            )}
            {w.work_location && (
              <InfoRow
                label="Location"
                value={
                  <span className="flex items-center gap-1">
                    <MapPin size={12} className="text-slate-400" />
                    {w.work_location}
                  </span>
                }
              />
            )}
          </Section>

          {/* Personal Info */}
          {(w.date_of_birth || w.gender) && (
            <Section icon={<User size={16} className="text-green-500" />} title="Personal Information" testId="personal-details">
              {w.date_of_birth && (
                <InfoRow
                  label="Date of Birth"
                  value={new Date(w.date_of_birth).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}
                />
              )}
              {w.gender && (
                <InfoRow
                  label="Gender"
                  value={w.gender.charAt(0) + w.gender.slice(1).toLowerCase().replace(/_/g, " ")}
                />
              )}
            </Section>
          )}

          {/* Status History */}
          {statusHistory.length > 0 && (
            <Section icon={<Clock size={16} className="text-amber-500" />} title="Employment Status History" testId="status-history">
              {statusHistory.slice(0, 3).map((s, i) => (
                <div key={i} className="flex items-center justify-between py-1 border-b border-slate-100 last:border-0">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${STATUS_BADGE[s.status_code] || ""}`}>
                    {s.status_code}
                  </span>
                  <span className="text-xs text-slate-500">{s.reason || "—"}</span>
                </div>
              ))}
            </Section>
          )}
        </div>

        {/* Compensation Section — collapsible, permission-gated */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="compensation-section">
          <button
            onClick={fetchCompensation}
            className="w-full p-5 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left"
            data-testid="compensation-toggle"
          >
            <TrendingUp size={16} className="text-teal-500" />
            <span className="font-semibold text-slate-800 flex-1">Compensation Overview</span>
            {compLoading ? (
              <span className="text-xs text-slate-400">Loading…</span>
            ) : (
              <>
                <ShieldCheck size={14} className="text-slate-400" />
                <span className="text-xs text-slate-400 mr-1">Authorized access only</span>
                {showCompensation ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
              </>
            )}
          </button>

          {compError && (
            <div className="px-5 pb-5 text-sm text-red-600 flex items-center gap-2" data-testid="comp-error">
              <AlertCircle size={14} /> {compError}
            </div>
          )}

          {showCompensation && compensation && (
            <CompensationView compensation={compensation} formatCurrency={formatCurrency} />
          )}
        </div>
      </div>
    </div>
  );
}

function CompensationView({ compensation: c, formatCurrency }) {
  const baseSalary = c.components.find((comp) => comp.component_code === "BASE_SALARY");
  const allowances = c.components.filter((comp) => comp.component_code !== "BASE_SALARY");

  return (
    <div className="border-t border-slate-200 p-5 space-y-5" data-testid="compensation-view">
      {/* CTC Banner */}
      <div className="bg-teal-50 rounded-lg p-4 flex items-center justify-between border border-teal-200">
        <div>
          <p className="text-xs text-teal-600 font-medium uppercase tracking-wide">Total Monthly CTC</p>
          <p className="text-2xl font-bold text-teal-900">{formatCurrency(c.total_compensation)}</p>
        </div>
        <div className="text-right text-xs text-teal-600">
          <p>{c.grade_name?.split("—")[1]?.trim()}</p>
          <p>{c.pay_frequency}</p>
          <p className="text-teal-400">Effective {new Date(c.effective_from).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}</p>
        </div>
      </div>

      {/* Component Breakdown */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Component Breakdown</p>
        <div className="space-y-2">
          {c.components.map((comp) => {
            const pct = Math.round((comp.amount / c.total_compensation) * 100);
            return (
              <div key={comp.component_code} data-testid={`comp-component-${comp.component_code}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-700">{COMPONENT_LABELS[comp.component_code] || comp.component_name}</span>
                  <span className="font-semibold text-slate-900">{formatCurrency(comp.amount)}</span>
                </div>
                <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${comp.component_code === "BASE_SALARY" ? "bg-teal-500" : "bg-teal-200"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
        Compensation amounts are synthetic / representative (PEO-01 demo). Not authoritative for payroll or statutory submissions.
      </p>
    </div>
  );
}

function Section({ icon, title, children, testId }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h3 className="font-semibold text-slate-800 text-sm">{title}</h3>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-xs text-slate-500 mt-0.5 flex-shrink-0">{label}</span>
      <span className="text-sm text-slate-800 text-right">{value || "—"}</span>
    </div>
  );
}
